/**
 * Power panel — spec §5.
 *
 * §5 is explicit that it "should be obvious when power is the bottleneck",
 * so this panel is deliberately the loudest thing in the sidebar: a bar that
 * fills and turns amber, then red, as demand outruns capacity, with the
 * throttle percentage stated in words rather than left to be inferred from a
 * ratio.
 *
 * There is no failure state to report — spec §5 has no blackout, only smooth
 * slowdown — so the copy says "everything is running at N%" rather than
 * anything alarming.
 */

import type { GameState } from '../../sim/types';
import { el, section, setText, type Panel } from './panel';

export function createPowerPanel(): Panel {
  const { root, body } = section('Power');

  const barTrack = el('div', 'bar-track');
  const barFill = el('div', 'bar-fill');
  barTrack.append(barFill);

  const headline = el('div', 'power-headline');

  const capacityRow = el('div', 'row');
  capacityRow.append(el('span', 'row-label', 'Capacity'));
  const capacityValue = el('span', 'row-value');
  capacityRow.append(capacityValue);

  const demandRow = el('div', 'row');
  demandRow.append(el('span', 'row-label', 'Demand'));
  const demandValue = el('span', 'row-value');
  demandRow.append(demandValue);

  const mechRow = el('div', 'row');
  mechRow.append(el('span', 'row-label', 'Mechs'));
  const mechValue = el('span', 'row-value');
  mechRow.append(mechValue);

  body.append(headline, barTrack, capacityRow, demandRow, mechRow);

  return {
    root,
    update(state: GameState): void {
      const { capacity, demand, throttleBp } = state.power;
      const percent = Math.round(throttleBp / 100);

      setText(capacityValue, String(capacity));
      setText(demandValue, String(demand));
      setText(mechValue, `${state.mechs.assigned}/${state.mechs.total}`);

      if (percent >= 100) {
        setText(headline, 'Unthrottled');
        headline.className = 'power-headline';
      } else {
        setText(headline, `Throttled — everything running at ${percent}%`);
        headline.className = percent < 50 ? 'power-headline danger' : 'power-headline warn';
      }

      // The bar shows demand against capacity, clamped: at 80x overdraw an
      // unclamped bar would say nothing that "100%" does not.
      const load = capacity <= 0 ? 1 : Math.min(1, demand / capacity);
      barFill.style.width = `${(load * 100).toFixed(1)}%`;
      barFill.className =
        percent >= 100 ? 'bar-fill' : percent < 50 ? 'bar-fill danger' : 'bar-fill warn';
    },
  };
}
